import { Card } from 'primereact/card';
import { InputText } from 'primereact/inputtext';
import { InputTextarea } from 'primereact/inputtextarea';
import { TipTapEditor } from '../Common/TipTapEditor';
import { TipTapToolbar } from '../Common/TipTapToolbar';
import { Button } from 'primereact/button';
import { Tooltip } from 'primereact/tooltip';
import { Chip, ChipRemoveEvent } from 'primereact/chip';
import { AutoComplete } from 'primereact/autocomplete';
import { Checkbox } from 'primereact/checkbox';
import { Dropdown } from 'primereact/dropdown';
import { Accordion, AccordionTab } from 'primereact/accordion';
import { Dialog } from 'primereact/dialog';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { useEffect, useState, useRef } from 'react';
import { ProgressSpinner } from 'primereact/progressspinner';
import { Toast } from 'primereact/toast';
import './MeasureDetailsComponent.scss';
import { useAuthContext } from '../../Context/AuthContext';
import httpService from '../../services/httpService';
import { ApiEndpoints, Constants } from '../../constants/appConstants';
import {
  Reason,
  MeasureStandardDetails,
  SMEDetailsDTO,
  ContractTimeframeDTO,
  ReviewCycleQuestionData,
  MeasureCommentDTO
} from './model';
import { useLocation, useNavigate } from 'react-router';
import ComponentHeader from '../../pages/ComponentHeader';
import CommentsSidebar from '../ProviderProfile/CommentsSidebar';
import { exportToExcel } from '../../services/excelExportService';
import { formatToLocalDateTime } from '../../services/dateTimeService';
import { sanitize } from '../../services/renderingService';
import logger from '../../utils/logger';

export const MeasureDetailsComponent = () => {
  const toast = useRef<Toast>(null);

  const [form, setForm] = useState<MeasureStandardDetails>({
    standardMeasureId: 0,
    tierName: '',
    providerType: '',
    performanceArea: '',
    standardId: 0,
    measureId: 0,
    measureCode: '',
    measureDescription: '',
    measureStatus: '',
    measureStatusId: 0,
    scoringCriteria: ``,
    standardDefinition: '',
    providerProcessDetails: '',
    dataType: '',
    dataSource: '',
    dataOwner: '',
    keyRequiredFields: '',
    calculationDetails: '',
    futureConsiderations: '',
    smeDetails: [],
    reviewReasons: [],
    isCompositeMeasure: false,
    compositeGroupId: null,
    groupName: null,
    contractTimeframeId: null,
    contractTimeframe: null
  });

  const [measureCommentsVisible, setMeasureCommentsVisible] = useState(false);
  const [measureComments, setMeasureComments] = useState<MeasureCommentDTO[]>([]);
  const [measureCommentsLoading, setMeasureCommentsLoading] = useState(false);
  const [selectedCommentYear, setSelectedCommentYear] = useState<string | number>('');
  const [smeQuery, setSmeQuery] = useState('');
  const [smeOptions, setSmeOptions] = useState<any[]>([]);
  const [smeUsers, setSmeUsers] = useState<SMEDetailsDTO[]>([]);
  const [contractTimeframes, setContractTimeframes] = useState<ContractTimeframeDTO[]>([]);
  const [reviewCycleData, setReviewCycleData] = useState<ReviewCycleQuestionData>({});
  const [isLibraryLocked, setIsLibraryLocked] = useState(true);
  const [sidebarVisible, setSidebarVisible] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [compositeMeasureOptions, setCompositeMeasureOptions] = useState<
    { label: string; value: number }[]
  >([]);

  const navigate = useNavigate();
  const location = useLocation();
  const { isAdministrator } = useAuthContext();

  const requiredFields: Array<{
    key: keyof MeasureStandardDetails;
    label: string;
  }> = [
    {
      key: 'measureDescription',
      label: 'Measure Description'
    }
  ];

  const items = [
    {
      label: 'PBC Standard & Measures',
      path: '/standards'
    },
    {
      label: 'Performance Measure Data Dictionary',
      path: ''
    }
  ];

  const searchSMEs = (event: { query: string }) => {
    setSmeOptions(
      smeUsers.filter((sme) =>
        sme.smeName
          .toLowerCase()
          .includes(event.query.toLowerCase())
      )
    );
  };

  useEffect(() => {
    GetMeasureStandardDetails();
    GetAllSmeUsers();
    getLibraryLockFeature();
    GetCompositeMeasureGroups();
    GetAllContractTimeframes();
  }, []);

  const GetMeasureStandardDetails = () => {
    httpService
      .get(
        `${ApiEndpoints.GetMeasureStandardDetails}/${location.state.standardMeasureId}`
      )
      .then((resp: any) => {
        const data = resp.data;

        const convertToDate = (val: any) => {
          if (!val) {
            return null;
          }

          if (val instanceof Date) {
            return val;
          }

          const d = new Date(val);

          return isNaN(d.getTime()) ? null : d;
        };

        setForm({
          ...data,
          providerProcessDetails: sanitize(
            data.providerProcessDetails || ''
          ),
          scoringCriteria: sanitize(
            data.scoringCriteria || ''
          ),
          contractStartDate: convertToDate(
            data.contractStartDate
          ),
          contractEndDate: convertToDate(
            data.contractEndDate
          ),
          isCompositeMeasure: data.compositeGroupId
            ? true
            : data.isCompositeMeasure
        });

        GetQuestionDataForStandardAssociation(
          location.state.providerType,
          location.state.standardMeasureId
        );
      })
      .catch((error: any) => {
        logger.error(
          'MeasureDetailsComponent.GetMeasureStandardDetails failed',
          error
        );

        if (
          error?.status === 403 ||
          error?.status === 401 ||
          error?.response?.status === 403 ||
          error?.response?.status === 401
        ) {
          navigate('/unauthorized');
        }
      });
  };

  const GetQuestionDataForStandardAssociation = (
    providerType: string,
    standardId: number
  ) => {
    httpService
      .get(
        `${ApiEndpoints.GetQuestionDataForStandardAssociation}/${providerType}/${standardId}`
      )
      .then((resp: any) => {
        setReviewCycleData(resp.data || {});
      })
      .catch((error: any) => {
        logger.error(
          'MeasureDetailsComponent.GetQuestionDataForStandardAssociation failed',
          error
        );
      });
  };

  const downloadReviewPackage = (
    reviewYear: number,
    providerType: string,
    questionGuids: Array<string>
  ) => {
    httpService
      .postBlob(
        `${ApiEndpoints.GetProviderFileZip}/${reviewYear}/${providerType}`,
        questionGuids
      )
      .then((response: any) => {
        if (response?.status !== 200) {
          throw new Error(
            'Review package download failed.'
          );
        }

        const blob = new Blob([response.data]);

        if (blob.size === 0) {
          toast.current?.show({
            severity: 'warn',
            summary: 'No Files Found',
            detail: 'Measure doesnot have files',
            life: 3000
          });

          return;
        }

        const url =
          window.URL.createObjectURL(blob);

        const link =
          document.createElement('a');

        link.href = url;

        link.setAttribute(
          'download',
          `${form.measureCode}_${form.tierName}_${reviewYear}_DataPkg.zip`
        );

        document.body.appendChild(link);

        link.click();
        link.remove();

        window.URL.revokeObjectURL(url);

        toast.current?.show({
          severity: 'success',
          summary: 'File Download',
          detail: 'File download successful',
          life: 3000
        });
      })
      .catch((error: any) => {
        logger.error(
          'MeasureDetailsComponent.downloadReviewPackage failed',
          error
        );

        toast.current?.show({
          severity: 'error',
          summary: 'Download Failed',
          detail:
            'File download failed. Please try again later',
          life: 3000
        });
      });
  };

  const GetAllSmeUsers = () => {
    httpService
      .get(ApiEndpoints.GetAllSmeUsers)
      .then((resp: any) => {
        setSmeUsers(resp.data);
      })
      .catch((error: any) => {
        logger.error(
          'MeasureDetailsComponent.GetAllSmeUsers failed',
          error
        );
      });
  };

  const GetAllContractTimeframes = () => {
    httpService
      .get(ApiEndpoints.GetAllContractTimeframes)
      .then((resp: any) => {
        setContractTimeframes(resp.data);
      })
      .catch((error: any) => {
        logger.error(
          'MeasureDetailsComponent.GetAllContractTimeframes failed',
          error
        );
      });
  };

  const getLibraryLockFeature = () => {
    httpService
      .get(
        `${ApiEndpoints.GetProviderFeature}/${location.state.providerType}/${Constants.MEASURE_LIBRARY_LOCKED}`
      )
      .then((response: any) => {
        if (response.status === 200) {
          setIsLibraryLocked(
            !response?.data.isEnabled
          );
        }
      })
      .catch((error: any) => {
        logger.error(
          'MeasureDetailsComponent.getLibraryLockFeature failed',
          error
        );
      });
  };

  const fetchMeasureComments = (
    year: string | number
  ) => {
    setMeasureCommentsVisible(true);
    setMeasureCommentsLoading(true);
    setMeasureComments([]);
    setSelectedCommentYear(year);

    httpService
      .get(
        `${ApiEndpoints.GetMeasureComments}/${form.measureId}/${location.state.providerType}/${year}`
      )
      .then((resp: any) => {
        setMeasureComments(
          (resp.data as MeasureCommentDTO[]) || []
        );
      })
      .catch((error: any) => {
        logger.error(
          'MeasureDetailsComponent.fetchMeasureComments failed',
          error
        );

        setMeasureComments([]);
      })
      .finally(() => {
        setMeasureCommentsLoading(false);
      });
  };

  const GetCompositeMeasureGroups = () => {
    httpService
      .get(
        ApiEndpoints.GetCompositeMeasureGroups
      )
      .then((response: any) => {
        const options = response.data.map(
          (group: any) => ({
            label: group.groupName,
            value: group.compositeGroupId
          })
        );

        setCompositeMeasureOptions(options);
      })
      .catch((error: any) => {
        logger.error(
          'MeasureDetailsComponent.GetCompositeMeasureGroups failed',
          error
        );
      });
  };

  const saveMeasureDetails = async () => {
    const validationError =
      validateFields(requiredFields);

    if (validationError) {
      toast.current?.show({
        severity: 'error',
        summary: 'Validation',
        detail: validationError,
        life: 3000
      });

      return;
    }

    const sanitizedForm = {
      ...form,
      providerProcessDetails: sanitize(
        form.providerProcessDetails || ''
      ),
      scoringCriteria: sanitize(
        form.scoringCriteria || ''
      )
    };

    setIsSaving(true);

    try {
      const resp =
        await httpService.post(
          ApiEndpoints.UpdateMeasureStandardDetails,
          sanitizedForm
        );

      if (resp && resp.data === true) {
        toast.current?.show({
          severity: 'success',
          summary: 'Success',
          detail:
            'Measure details saved successfully',
          life: 3000
        });

        GetMeasureStandardDetails();
      } else {
        toast.current?.show({
          severity: 'error',
          summary: 'Error',
          detail:
            'Failed to save measure details',
          life: 3000
        });
      }
    } catch (error: any) {
      logger.error(
        'MeasureDetailsComponent.saveMeasureDetails failed',
        error
      );

      toast.current?.show({
        severity: 'error',
        summary: 'Error',
        detail:
          'An error occurred while saving',
        life: 3000
      });
    } finally {
      setIsSaving(false);
    }
  };

  const validateFields = (
    fields: Array<{
      key: keyof MeasureStandardDetails;
      label: string;
    }>
  ): string | null => {
    for (const field of fields) {
      const value = form[field.key];

      if (
        typeof value === 'string' &&
        (!value || value.trim() === '')
      ) {
        return `${field.label} is required.`;
      }

      if (
        value === null ||
        value === undefined
      ) {
        return `${field.label} is required.`;
      }
    }

    if (
      (form.reviewReasons || []).some(
        (reason) =>
          !reason.reasonText ||
          reason.reasonText.trim() === ''
      )
    ) {
      return 'All Reasons for Failure must be filled in.';
    }

    if (
      form.isCompositeMeasure &&
      !form.compositeGroupId
    ) {
      return 'Please select a composite measure group.';
    }

    return null;
  };

  const editorHeader = (editor: any) => {
    return (
      <div className="tiptap-toolbar">
        <TipTapToolbar
          editor={editor}
          disabled={!isAdministrator()}
        />
      </div>
    );
  };

  return (
    <div
      style={{
        marginTop: '42px',
        marginBottom: '50px',
        marginLeft: '5px',
        position: 'relative'
      }}
    >
      <ComponentHeader
        breadcrumbs={items}
        title="Performance Measure Data Dictionary"
        reviewYear={
          location.state.reviewYear
        }
      />

      <Card className="measure-details-card">
        <div className="buttonSection">
          <Button
            icon="pi pi-file"
            className="p-button-text"
            label="View Comments Log"
            style={{ marginRight: 16 }}
            onClick={() =>
              setSidebarVisible(true)
            }
          />

          <Button
            label={
              isSaving
                ? 'Saving...'
                : 'Save Changes'
            }
            icon={
              isSaving
                ? 'pi pi-spin pi-spinner'
                : 'pi pi-check'
            }
            className="p-button-success"
            onClick={saveMeasureDetails}
            disabled={isSaving}
          />
        </div>

        <div
          style={{
            background: '#e5e5e5',
            padding: 10,
            fontWeight: 700,
            fontSize: 20,
            borderRadius: 5,
            marginBottom: 10
          }}
        >
          {form.measureCode ||
            '<Measure Code>'}
        </div>

        <div
          className="measure-details-form-row"
          style={{
            display: 'flex',
            gap: 16
          }}
        >
          <div
            style={{
              flex: 1,
              display: 'flex',
              alignItems: 'center',
              gap: 8
            }}
          >
            <label
              htmlFor="performanceArea"
              className="measure-details-label"
              style={{
                minWidth: 120,
                whiteSpace: 'nowrap'
              }}
            >
              Performance Area:
            </label>

            <Tooltip
              target=".performance-area-wrapper"
              position="bottom"
              className="no-arrow-tooltip"
            />

            <div
              className="performance-area-wrapper"
              style={{ flex: 1 }}
              data-pr-tooltip={`${location.state.performanceAreaTitle} : ${form.performanceArea}`}
            >
              <InputText
                id="performanceArea"
                value={`${location.state.performanceAreaTitle} : ${form.performanceArea}`}
                className="measure-details-input"
                style={{
                  width: '100%',
                  textOverflow: 'ellipsis',
                  overflow: 'hidden',
                  whiteSpace: 'nowrap',
                  cursor: 'default'
                }}
                disabled
              />
            </div>
          </div>

          <div
            style={{
              flex: 1,
              display: 'flex',
              alignItems: 'center',
              gap: 8
            }}
          >
            <label
              htmlFor="service"
              className="measure-details-label"
              style={{
                minWidth: 70,
                whiteSpace: 'nowrap'
              }}
            >
              PBC Type:
            </label>

            <InputText
              id="service"
              value={`${location.state.providerTypeName} : ${form.tierName}`}
              className="measure-details-input"
              style={{ flex: 1 }}
              disabled
            />
          </div>

          <div
            style={{
              flex: 1,
              display: 'flex',
              alignItems: 'center',
              gap: 8
            }}
          >
            <label
              htmlFor="measureStatus"
              className="measure-details-label"
              style={{
                minWidth: 110,
                whiteSpace: 'nowrap'
              }}
            >
              Measure Status:
            </label>

            <Dropdown
              id="measureStatus"
              value={
                form.measureStatusId || null
              }
              options={
                location.state
                  ?.measureStatusOptions ||
                []
              }
              optionLabel="label"
              optionValue="id"
              onChange={(e) => {
                const selectedOption = (
                  location.state
                    ?.measureStatusOptions ||
                  []
                ).find(
                  (opt: any) =>
                    opt.id === e.value
                );

                setForm({
                  ...form,
                  measureStatusId:
                    e.value,
                  measureStatus:
                    selectedOption?.label ||
                    ''
                });
              }}
              placeholder="Select Status"
              className="measure-details-input"
              style={{ flex: 1 }}
              disabled={!isAdministrator()}
            />
          </div>
        </div>

        <div className="measure-details-form-row">
          <div className="measure-input-field">
            <label
              htmlFor="definitionOfStandard"
              className="measure-details-label"
            >
              Definition of Standard:
            </label>

            <InputTextarea
              id="definitionOfStandard"
              value={
                form.standardDefinition ||
                ''
              }
              onChange={(e) =>
                setForm({
                  ...form,
                  standardDefinition:
                    e.target.value
                })
              }
              className="measure-details-input"
              readOnly={
                !isAdministrator() ||
                isLibraryLocked
              }
            />
          </div>
        </div>

        <div className="measure-details-form-row">
          <div className="measure-input-field">
            <label
              htmlFor="measureDescription"
              className="measure-details-label"
            >
              Measure Description:
            </label>

            <InputTextarea
              id="measureDescription"
              value={
                form.measureDescription ||
                ''
              }
              onChange={(e) =>
                setForm({
                  ...form,
                  measureDescription:
                    e.target.value
                })
              }
              className="measure-details-input"
              readOnly={
                !isAdministrator() ||
                isLibraryLocked
              }
              rows={4}
            />
          </div>
        </div>

        <div
          className="measure-details-form-row"
          style={{
            display: 'flex',
            flexWrap: 'nowrap',
            alignItems: 'flex-start',
            gap: 24
          }}
        >
          <div
            style={{
              flex: 1,
              display: 'flex',
              alignItems: 'center',
              gap: 4
            }}
          >
            <label
              htmlFor="smeName"
              className="measure-details-label"
              style={{
                whiteSpace: 'nowrap'
              }}
            >
              SME Name:
            </label>

            <AutoComplete
              id="smeName"
              value={smeQuery}
              suggestions={smeOptions}
              completeMethod={searchSMEs}
              field="smeName"
              onChange={(e) =>
                setSmeQuery(e.value)
              }
              onSelect={(e) => {
                const exists =
                  form.smeDetails?.some(
                    (sme) =>
                      sme.smeIdentifier ===
                        e.value
                          .smeIdentifier &&
                      sme.isActive
                  );

                if (!exists) {
                  const { ...smeData } =
                    e.value;

                  setForm({
                    ...form,
                    smeDetails: [
                      ...(form.smeDetails ||
                        []),
                      {
                        ...smeData,
                        isActive: true,
                        measureId:
                          form.measureId
                      }
                    ]
                  });
                }

                setSmeQuery('');
              }}
              forceSelection
              placeholder="Search and select SME"
              disabled={
                !isAdministrator() ||
                isLibraryLocked
              }
              className="measure-details-input"
              style={{ flex: 1 }}
            />

            <div
              style={{
                minHeight: 36,
                background: '#f8f9fa',
                border:
                  '1px solid #e0e0e0',
                borderRadius: 8,
                padding: '4px 8px',
                display: 'flex',
                flexWrap: 'wrap',
                gap: 4,
                boxSizing: 'border-box',
                maxHeight: 80,
                overflowY: 'auto',
                transition:
                  'box-shadow 0.2s',
                flex: 1
              }}
            >
              {(form.smeDetails || [])
                .filter(
                  (sme) => sme.isActive
                )
                .map((sme) => (
                  <Chip
                    key={
                      sme.smeAssociationId ??
                      sme.smeIdentifier
                    }
                    label={sme.smeName}
                    removable={
                      isAdministrator() &&
                      !isLibraryLocked
                    }
                    onRemove={(
                      _event: ChipRemoveEvent
                    ) => {
                      setForm({
                        ...form,
                        smeDetails: (
                          form.smeDetails ||
                          []
                        ).filter((s) => {
                          if (
                            s.smeAssociationId
                          ) {
                            if (
                              s.smeAssociationId ===
                              sme.smeAssociationId
                            ) {
                              s.isActive =
                                false;
                            }

                            return true;
                          }

                          return (
                            s.smeIdentifier !==
                            sme.smeIdentifier
                          );
                        })
                      });

                      return true;
                    }}
                    style={{
                      marginBottom: 2
                    }}
                  />
                ))}
            </div>
          </div>

          <div
            style={{
              flex: 1,
              display: 'flex',
              alignItems: 'center',
              gap: 8
            }}
          >
            <label
              htmlFor="contractTimeframe"
              className="measure-details-label"
              style={{
                minWidth: 130,
                whiteSpace: 'nowrap'
              }}
            >
              Contract Timeframe:
            </label>

            <Dropdown
              id="contractTimeframe"
              value={
                form.contractTimeframeId
              }
              options={
                contractTimeframes || []
              }
              optionLabel="contractTimeframe"
              optionValue="contractTimeframeId"
              onChange={(e) => {
                const selectedOption =
                  contractTimeframes.find(
                    (opt: any) =>
                      opt.contractTimeframeId ===
                      e.value
                  );

                setForm({
                  ...form,
                  contractTimeframeId:
                    e.value,
                  contractTimeframe:
                    selectedOption
                      ?.contractTimeframe ||
                    ''
                });
              }}
              placeholder="Select Timeframe"
              style={{ flex: 1 }}
              disabled={
                !isAdministrator() ||
                isLibraryLocked
              }
            />
          </div>
        </div>

        <div
          className="measure-details-form-row"
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 16
          }}
        >
          <label
            className="measure-details-label"
            style={{
              minWidth: 'auto'
            }}
          >
            Is this part of a composite
            measure?
          </label>

          <Checkbox
            inputId="isCompositeMeasure"
            checked={
              form.isCompositeMeasure ||
              false
            }
            onChange={(e) =>
              setForm({
                ...form,
                isCompositeMeasure:
                  e.checked,
                compositeGroupId:
                  e.checked
                    ? form.compositeGroupId
                    : null,
                groupName: e.checked
                  ? form.groupName
                  : null
              })
            }
            disabled={!isAdministrator()}
          />

          <label
            htmlFor="isCompositeMeasure"
            style={{ marginLeft: 4 }}
          >
            Yes
          </label>

          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              marginLeft: 'auto'
            }}
          >
            <label
              className="measure-details-label"
              style={{
                minWidth: 'auto'
              }}
            >
              Select which composite it is
              a part of:
            </label>

            <Dropdown
              value={
                form.compositeGroupId
              }
              options={
                compositeMeasureOptions
              }
              optionLabel="label"
              optionValue="value"
              onChange={(e) => {
                const selectedOption =
                  compositeMeasureOptions.find(
                    (opt) =>
                      opt.value ===
                      e.value
                  );

                setForm({
                  ...form,
                  compositeGroupId:
                    e.value,
                  groupName:
                    selectedOption?.label ||
                    null
                });
              }}
              placeholder="Select"
              style={{ minWidth: 200 }}
              disabled={
                !form.isCompositeMeasure ||
                !isAdministrator()
              }
            />
          </div>
        </div>

        <div
          style={{
            background: '#e5e5e5',
            padding: 10,
            fontWeight: 700,
            fontSize: 18,
            borderRadius: 5,
            margin: '16px 0 10px 0'
          }}
        >
          Measure Details
        </div>

        <div className="measure-details-form-row">
          <label
            htmlFor="expectedProviderResponse"
            className="measure-details-label"
            style={{
              minWidth: 320,
              alignSelf: 'flex-start'
            }}
          >
            Process Details:
          </label>

          <TipTapEditor
            id="expectedProviderResponse"
            value={
              form.providerProcessDetails ||
              ''
            }
            headerTemplate={
              editorHeader
            }
            onTextChange={(e) =>
              setForm({
                ...form,
                providerProcessDetails:
                  sanitize(
                    e.htmlValue ?? ''
                  )
              })
            }
            style={{ height: '20vh' }}
            className="measure-details-input"
            readOnly={
              !isAdministrator()
            }
          />
        </div>

        <div className="measure-details-form-row">
          <label
            htmlFor="scoringGuidance"
            className="measure-details-label"
            style={{
              minWidth: 320,
              alignSelf: 'flex-start'
            }}
          >
            Scoring Guidance:
          </label>

          <div style={{ flex: 1 }}>
            <TipTapEditor
              key={
                isLibraryLocked
                  ? 'readonly'
                  : 'editable'
              }
              id="scoringGuidance"
              value={
                form.scoringCriteria ||
                ''
              }
              headerTemplate={
                editorHeader
              }
              onTextChange={(e) =>
                setForm({
                  ...form,
                  scoringCriteria:
                    sanitize(
                      e.htmlValue ?? ''
                    )
                })
              }
              style={{
                height: '20vh'
              }}
              className="measure-details-input"
              readOnly={
                !isAdministrator() ||
                isLibraryLocked
              }
            />
          </div>
        </div>

        <div className="measure-details-form-row">
          <label
            htmlFor="reasonsForFailure"
            className="measure-details-label"
            style={{
              minWidth: 320,
              alignSelf: 'flex-start'
            }}
          >
            Reasons for Failure:
          </label>

          <div style={{ flex: 1 }}>
            {(form.reviewReasons || []).map(
              (
                reason: Reason,
                index: number
              ) => (
                <div
                  key={reason.reasonId}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    marginBottom: 8,
                    gap: 8
                  }}
                >
                  <InputText
                    value={
                      reason.reasonText
                    }
                    onChange={(e) => {
                      const newReasons = [
                        ...(form.reviewReasons ||
                          [])
                      ];

                      newReasons[index] = {
                        ...newReasons[index],
                        reasonCode:
                          e.target.value,
                        reasonText:
                          e.target.value
                      };

                      setForm({
                        ...form,
                        reviewReasons:
                          newReasons
                      });
                    }}
                    placeholder={`Reason ${
                      index + 1
                    }`}
                    style={{
                      flex: 1
                    }}
                    className="measure-details-input"
                    readOnly={
                      !isAdministrator() ||
                      isLibraryLocked
                    }
                  />

                  <button
                    className="pi pi-times close-button"
                    onClick={() => {
                      const newReasons = (
                        form.reviewReasons ||
                        []
                      ).filter(
                        (_, i) =>
                          i !== index
                      );

                      setForm({
                        ...form,
                        reviewReasons:
                          newReasons
                      });
                    }}
                    style={{
                      minWidth: 'auto',
                      fontSize: 'x-large'
                    }}
                    disabled={
                      !isAdministrator() ||
                      isLibraryLocked
                    }
                  />
                </div>
              )
            )}

            <Button
              icon="pi pi-plus"
              label="Add Reason"
              className="p-button-sm p-button-secondary"
              onClick={() =>
                setForm({
                  ...form,
                  reviewReasons: [
                    ...(form.reviewReasons ||
                      []),
                    {
                      reasonId:
                        crypto.randomUUID(),
                      reasonCode: '',
                      reasonText: ''
                    }
                  ]
                })
              }
              style={{
                marginTop: 8
              }}
              disabled={
                !isAdministrator() ||
                isLibraryLocked
              }
            />
          </div>
        </div>

        <div
          style={{
            background: '#e5e5e5',
            padding: 10,
            fontWeight: 700,
            fontSize: 18,
            borderRadius: 5,
            margin: '16px 0 10px 0'
          }}
        >
          Data Details
        </div>

        <div className="measure-details-form-row">
          <div className="measure-input-field">
            <label
              htmlFor="dataType"
              className="measure-details-label"
              style={{
                minWidth: 330
              }}
            >
              Data Type:
            </label>

            <InputText
              id="dataType"
              value={
                form.dataType || ''
              }
              onChange={(e) =>
                setForm({
                  ...form,
                  dataType:
                    e.target.value
                })
              }
              className="measure-details-input"
            />
          </div>

          <div className="measure-input-field">
            <label
              htmlFor="dataOwner"
              className="measure-details-label"
              style={{
                minWidth: 280
              }}
            >
              Data Owner:
            </label>

            <InputText
              id="dataOwner"
              value={
                form.dataOwner || ''
              }
              onChange={(e) =>
                setForm({
                  ...form,
                  dataOwner:
                    e.target.value
                })
              }
              className="measure-details-input"
            />
          </div>
        </div>

        <div className="measure-details-form-row">
          <label
            htmlFor="dataSources"
            className="measure-details-label"
            style={{
              minWidth: 320
            }}
          >
            Data Source(s):
          </label>

          <InputTextarea
            id="dataSources"
            value={
              form.dataSource || ''
            }
            onChange={(e) =>
              setForm({
                ...form,
                dataSource:
                  e.target.value
              })
            }
            className="measure-details-input"
          />
        </div>

        <div className="measure-details-form-row">
          <label
            htmlFor="keyRequiredFields"
            className="measure-details-label"
            style={{
              minWidth: 320,
              alignSelf: 'flex-start'
            }}
          >
            Key Required Fields:
          </label>

          <InputTextarea
            id="keyRequiredFields"
            value={
              form.keyRequiredFields ||
              ''
            }
            onChange={(e) =>
              setForm({
                ...form,
                keyRequiredFields:
                  e.target.value
              })
            }
            className="measure-details-input"
            rows={3}
          />
        </div>

        <div className="measure-details-form-row">
          <label
            htmlFor="calculationDetails"
            className="measure-details-label"
            style={{
              minWidth: 320,
              alignSelf: 'flex-start'
            }}
          >
            Calculations Details:
          </label>

          <InputTextarea
            id="calculationDetails"
            value={
              form.calculationDetails ||
              ''
            }
            onChange={(e) =>
              setForm({
                ...form,
                calculationDetails:
                  e.target.value
              })
            }
            className="measure-details-input"
            rows={3}
          />
        </div>

        <div className="measure-details-form-row">
          <label
            htmlFor="futureConsiderations"
            className="measure-details-label"
            style={{
              minWidth: 320,
              alignSelf: 'flex-start'
            }}
          >
            Future Considerations:
          </label>

          <InputTextarea
            id="futureConsiderations"
            value={
              form.futureConsiderations ||
              ''
            }
            onChange={(e) =>
              setForm({
                ...form,
                futureConsiderations:
                  e.target.value
              })
            }
            className="measure-details-input"
            rows={3}
          />
        </div>

        <div
          style={{
            background: '#e5e5e5',
            padding: 10,
            fontWeight: 700,
            fontSize: 18,
            borderRadius: 5,
            margin: '16px 0 10px 0'
          }}
        >
          Review Cycle Documents &amp;
          Comments
        </div>

        <Accordion
          multiple
          className="review-cycle-accordion"
        >
          {Object.keys(reviewCycleData)
            .sort(
              (a, b) =>
                Number(b) - Number(a)
            )
            .map((year) => (
              <AccordionTab
                key={year}
                header={`${year} Review Cycle`}
              >
                <div className="review-cycle-comments-row">
                  <span
                    className="measure-details-label"
                    style={{
                      minWidth: 'auto',
                      fontWeight: 600
                    }}
                  >
                    Reviewer Comments:
                  </span>

                  <i className="pi pi-file review-cycle-comment-icon" />

                  <a
                    href="#"
                    className="review-cycle-comments-link"
                    onClick={(e) => {
                      e.preventDefault();

                      fetchMeasureComments(
                        year
                      );
                    }}
                  >
                    View All Comments
                  </a>
                </div>

                {reviewCycleData[year]
                  .length > 0 && (
                  <table className="review-cycle-questions-table">
                    <thead>
                      <tr>
                        <th>
                          Question
                        </th>
                        <th>
                          Download all
                          Provider Files
                        </th>
                      </tr>
                    </thead>

                    <tbody>
                      {reviewCycleData[
                        year
                      ].map(
                        (question) => (
                          <tr
                            key={
                              question.questionId
                            }
                          >
                            <td>
                              <strong>
                                Question{' '}
                                {
                                  question.questionTitle
                                }
                                :
                              </strong>{' '}
                              {
                                question.questionText
                              }
                            </td>

                            <td>
                              <Button
                                icon="pi pi-download"
                                label="Download Files"
                                className="p-button-secondary download-files-button"
                                onClick={() =>
                                  downloadReviewPackage(
                                    Number(
                                      year
                                    ),
                                    location
                                      .state
                                      .providerType,
                                    [
                                      question.questionGuid
                                    ]
                                  )
                                }
                              />
                            </td>
                          </tr>
                        )
                      )}
                    </tbody>
                  </table>
                )}
              </AccordionTab>
            ))}
        </Accordion>
      </Card>

      <Dialog
        header={`Comments for Measure ${form.measureCode} (${form.tierName})`}
        visible={
          measureCommentsVisible
        }
        onHide={() =>
          setMeasureCommentsVisible(
            false
          )
        }
        style={{
          width: '40vw',
          minWidth: '500px'
        }}
        modal
        dismissableMask
        className="activity-log-dialog"
        footer={
          <div
            style={{
              display: 'flex',
              justifyContent:
                'flex-end'
            }}
          >
            <Button
              icon="pi pi-file-excel"
              label="Export Comments"
              className="p-button-success p-button-sm"
              disabled={
                measureComments.length ===
                0
              }
              onClick={() =>
                exportToExcel(
                  [
                    {
                      header:
                        'DATE/TIME (LOCAL)',
                      key:
                        'createdDate',
                      width: 22
                    },
                    {
                      header: 'MPI',
                      key:
                        'providerMPI',
                      width: 22
                    },
                    {
                      header: 'USER',
                      key:
                        'commentAddedBy',
                      width: 24
                    },
                    {
                      header:
                        'COMMENT',
                      key:
                        'commentDescription',
                      width: 40
                    }
                  ],
                  measureComments.map(
                    (c) => ({
                      createdDate:
                        c.createdDate
                          ? formatToLocalDateTime(
                              c.createdDate
                            )
                          : '-',
                      providerMPI:
                        c.providerMPI ??
                        '-',
                      commentAddedBy:
                        c.commentAddedBy ??
                        '-',
                      commentDescription:
                        c.commentDescription ??
                        '-'
                    })
                  ),
                  `${form.measureCode}_${form.tierName}_${selectedCommentYear}_Comments`
                )
                  .then(() =>
                    toast.current?.show(
                      {
                        severity:
                          'success',
                        summary:
                          'File Download',
                        detail:
                          'File download successful',
                        life: 3000
                      }
                    )
                  )
                  .catch(
                    (error: any) => {
                      logger.error(
                        'MeasureDetailsComponent.exportComments failed',
                        error
                      );

                      toast.current?.show(
                        {
                          severity:
                            'error',
                          summary:
                            'Download Failed',
                          detail:
                            'File download failed. Please try again later',
                          life: 3000
                        }
                      );
                    }
                  )
              }
            />
          </div>
        }
      >
        <div className="activity-log-table-wrapper">
          <DataTable
            value={measureComments}
            loading={
              measureCommentsLoading
            }
            sortField="createdDate"
            sortOrder={-1}
            emptyMessage="No comments found."
            size="small"
            scrollable
            scrollHeight="300px"
          >
            <Column
              field="createdDate"
              header="DATE/TIME (LOCAL)"
              sortable
              body={(
                row: MeasureCommentDTO
              ) => {
                if (!row.createdDate) {
                  return '-';
                }

                return formatToLocalDateTime(
                  row.createdDate
                );
              }}
            />

            <Column
              field="providerMPI"
              header="MPI"
              sortable
              filter
              filterPlaceholder="Search MPI"
              body={(
                row: MeasureCommentDTO
              ) =>
                row.providerMPI ??
                '-'
              }
            />

            <Column
              field="commentAddedBy"
              header="USER"
              sortable
              filter
              filterPlaceholder="Search user"
              body={(
                row: MeasureCommentDTO
              ) =>
                row.commentAddedBy ??
                '-'
              }
            />

            <Column
              field="commentDescription"
              header="COMMENT"
              body={(
                row: MeasureCommentDTO
              ) =>
                row.commentDescription ??
                '-'
              }
            />
          </DataTable>
        </div>
      </Dialog>

      <Toast
        ref={toast}
        position="top-right"
        appendTo={document.body}
      />

      {sidebarVisible && (
        <CommentsSidebar
          {...{
            sidebarVisible,
            setSidebarVisible,
            handleCommentsSidebarHide:
              () =>
                setSidebarVisible(
                  false
                ),
            providerType:
              location.state
                .providerType,
            commentProviderMPI: '0',
            commentMeasureId:
              form.measureId,
            commentMeasureNumber:
              form.measureCode ||
              '',
            commentLevel:
              'measureDetails',
            updatePerformanceAreasForComments:
              () => {},
            providerAgencyName: '',
            externalSave: () => {}
          }}
        />
      )}

      {isSaving && (
        <div
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background:
              'rgba(255,255,255,0.7)',
            zIndex: 1000,
            display: 'flex',
            alignItems: 'center',
            justifyContent:
              'center'
          }}
        >
          <ProgressSpinner
            strokeWidth="8"
          />
        </div>
      )}
    </div>
  );
};

export default MeasureDetailsComponent;